/**
 * Error Handler Utility
 *
 * Provides error classification, retry logic, and reporting integration.
 * Centralizes error handling for the application.
 *
 * @module lib/error-handler
 */

// ============================================
// Error Types
// ============================================

export enum ErrorType {
  NETWORK = 'NETWORK',           // Network failures, CORS, DNS, offline
  AUTH = 'AUTH',                 // 401 - authentication required/expired
  NOT_FOUND = 'NOT_FOUND',       // 404 - resource not found
  VALIDATION = 'VALIDATION',     // 400 - validation errors
  SERVER = 'SERVER',             // 5xx - server errors
  RATE_LIMIT = 'RATE_LIMIT',     // 429 - too many requests
  UNKNOWN = 'UNKNOWN',           // Everything else
}

export interface AppError extends Error {
  type: ErrorType
  status?: number
  details?: any
  originalError?: Error
}

// ============================================
// Error Classification
// ============================================

/**
 * Classifies an error into a specific error type.
 * Used to determine appropriate user messages and retry behavior.
 */
export function classifyError(error: any): AppError {
  // If it's already an AppError, return as-is
  if (error?.type && Object.values(ErrorType).includes(error.type)) {
    return error as AppError
  }

  const message = error?.message || error?.toString() || 'Unknown error'
  const status = error?.status || error?.response?.status

  let type = ErrorType.UNKNOWN

  if (status === 401) {
    type = ErrorType.AUTH
  } else if (status === 404) {
    type = ErrorType.NOT_FOUND
  } else if (status === 429) {
    type = ErrorType.RATE_LIMIT
  } else if (status && status >= 400 && status < 500) {
    type = ErrorType.VALIDATION
  } else if (status && status >= 500 && status < 600) {
    type = ErrorType.SERVER
  } else if (
    error?.code === 'NETWORK_ERROR' ||
    error?.code === 'ECONNABORTED' ||
    error?.code === 'ENOTFOUND' ||
    error?.code === 'ECONNREFUSED' ||
    typeof error.message === 'string' && (
      error.message.includes('NetworkError') ||
      error.message.includes('Failed to fetch') ||
      error.message.includes('Network request failed')
    )
  ) {
    type = ErrorType.NETWORK
  }

  const appError: AppError = new Error(message)
  appError.type = type
  appError.status = status
  appError.details = error
  appError.originalError = error

  return appError
}

// ============================================
// User-Friendly Messages
// ============================================

/**
 * Returns a user-friendly error message based on error type.
 * Used to show appropriate feedback in the UI.
 */
export function getUserErrorMessage(error: AppError, context?: string): string {
  const type = error.type

  switch (type) {
    case ErrorType.NETWORK:
      return 'Network error. Please check your internet connection and try again.'

    case ErrorType.AUTH:
      return 'Your session has expired. Please log in again.'

    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.'

    case ErrorType.VALIDATION:
      return 'Invalid request. Please check your input and try again.'

    case ErrorType.RATE_LIMIT:
      return 'Too many requests. Please wait a moment and try again.'

    case ErrorType.SERVER:
      return 'Server error. Our team has been notified. Please try again later.'

    default:
      return context
        ? `An error occurred in ${context}: ${error.message}`
        : `An unexpected error occurred: ${error.message}`
  }
}

// ============================================
// Retry Logic with Exponential Backoff
// ============================================

export interface RetryConfig {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffFactor?: number
  shouldRetry?: (error: AppError, attempt: number) => boolean
}

const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelay: 1000,  // 1 second
  maxDelay: 30000,     // 30 seconds
  backoffFactor: 2,
  shouldRetry: (error: AppError) =>
    error.type === ErrorType.NETWORK ||
    error.type === ErrorType.SERVER ||
    error.type === ErrorType.RATE_LIMIT ||
    error.status === 0, // Network error often returns status 0
}

/**
 * Executes an async function with retry logic.
 * Retries on network errors, server errors, and rate limits.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  config: RetryConfig = {}
): Promise<T> {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries!,
    initialDelay = DEFAULT_RETRY_CONFIG.initialDelay!,
    maxDelay = DEFAULT_RETRY_CONFIG.maxDelay!,
    backoffFactor = DEFAULT_RETRY_CONFIG.backoffFactor!,
    shouldRetry = DEFAULT_RETRY_CONFIG.shouldRetry!,
  } = config

  let lastError: any
  let attempt = 0

  while (attempt <= maxRetries) {
    try {
      return await fn()
    } catch (error: any) {
      lastError = error
      const appError = classifyError(error)

      // Check if we should retry
      if (attempt < maxRetries && shouldRetry(appError, attempt)) {
        const delay = Math.min(
          initialDelay * Math.pow(backoffFactor, attempt),
          maxDelay
        )

        console.warn(`[Retry] Attempt ${attempt + 1} failed. Retrying in ${delay}ms...`, {
          error: appError.message,
          type: appError.type,
        })

        await new Promise(resolve => setTimeout(resolve, delay))
        attempt++
        continue
      }

      // No more retries or shouldn't retry
      break
    }
  }

  // All retries exhausted or non-retryable error
  throw lastError
}

// ============================================
// Error Logging
// ============================================

/**
 * Logs an error to the console and optionally to an external service.
 * Integrates with Sentry if available.
 */
export function logError(error: any, context?: string, extra?: Record<string, any>): void {
  const appError = classifyError(error)

  // Always log to console in development
  if (import.meta.env.DEV) {
    console.error(`[Error] ${context || 'Unknown context'}:`, {
      message: appError.message,
      type: appError.type,
      status: appError.status,
      ...extra,
    })
  }

  // Send to Sentry if configured (for production)
  if (typeof window !== 'undefined' && (window as any).Sentry) {
    try {
      (window as any).Sentry.captureException(error, {
        extra: {
          context,
          ...extra,
        },
      })
    } catch (e) {
      // Silently fail - logging shouldn't crash the app
    }
  } else if (import.meta.env.PROD) {
    // TODO: In production without Sentry, could send to custom logging endpoint
    // fetch('/api/log', { method: 'POST', body: JSON.stringify({ error, context, extra }) })
  }
}

// ============================================
// API Error Handling
// ============================================

/**
 * Wraps an API fetch call with error classification and optional retry.
 * Use this instead of raw authFetch for resilient network requests.
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryConfig?: RetryConfig
): Promise<any> {
  return withRetry(async () => {
    try {
      const baseUrl = getBaseUrl()
      const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`

      const response = await fetch(fullUrl, options)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const error = new Error(`API Error ${response.status}: ${errorData.output || response.statusText}`)
        ;(error as any).status = response.status
        ;(error as any).details = errorData
        throw error
      }

      if (response.status === 204 || response.headers.get('Content-Length') === '0') {
        return {}
      }

      return response.json()
    } catch (error: any) {
      // Classify and re-throw
      throw classifyError(error)
    }
  }, retryConfig)
}

// Helper to get baseUrl (replicated from api.ts, but should be imported if needed)
function getBaseUrl(): string {
  if (typeof window === 'undefined') {
    return import.meta.env.VITE_API_URL || 'http://localhost:54002'
  }

  const envUrl = import.meta.env.VITE_API_URL
  if (envUrl) {
    return envUrl
  }

  return ''
}

// ============================================
// Global Error Handler (for unhandled errors)
// ============================================

/**
 * Sets up global error handlers for unhandled rejections and errors.
 * Call this once during app initialization.
 */
export function setupGlobalErrorHandlers(): void {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason
    logError(error, 'Unhandled Promise Rejection', {
      promise: event.promise?.toString(),
    })
    // Optionally show a user-friendly message
    console.error('Unhandled promise rejection:', error)
  })

  // Handle uncaught errors
  window.addEventListener('error', (event: ErrorEvent) => {
    logError(event.error, 'Uncaught Error', {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })
}
